import i18n, { type Resource, type ResourceLanguage } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { writeCrmLocale } from '../crm/crmStyling';

export const SUPPORTED_LANGUAGES = ['en', 'uk', 'ru', 'es'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';
export const LANGUAGE_STORAGE_KEY = 'onlymind_language';

export function isSupportedLanguage(value: unknown): value is SupportedLanguage {
  return typeof value === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

/**
 * Auto-load every namespace JSON under ./locales/<lng>/<namespace>.json.
 * Adding a new translated page is just a matter of dropping a new JSON file —
 * no manual registration here. Vite resolves this glob at build time.
 */
const localeModules = import.meta.glob<{ default: Record<string, unknown> }>(
  './locales/*/*.json',
  { eager: true },
);

const resources: Resource = {};
const namespaceSet = new Set<string>();

for (const path in localeModules) {
  // path looks like './locales/en/dashboard.json'
  const match = path.match(/\.\/locales\/([^/]+)\/([^/]+)\.json$/);
  if (!match) continue;
  const [, lng, ns] = match;
  if (!lng || !ns) continue;
  namespaceSet.add(ns);
  const langResources: ResourceLanguage = resources[lng] ?? (resources[lng] = {});
  langResources[ns] = localeModules[path].default;
}

export const NAMESPACES = Array.from(namespaceSet).sort();

// Resolve the initial language: persisted choice (localStorage) wins, otherwise
// the English default. The authoritative per-user value from the DB is applied
// after login via AuthContext.
function resolveInitialLanguage(): SupportedLanguage {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (isSupportedLanguage(stored)) return stored;
  } catch {
    // localStorage may be unavailable (e.g. private mode); fall back to default.
  }
  return DEFAULT_LANGUAGE;
}

i18n.use(initReactI18next).init({
  resources,
  lng: resolveInitialLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  ns: NAMESPACES,
  defaultNS: 'common',
  interpolation: {
    escapeValue: false, // React already escapes values
  },
  returnNull: false,
});

// One language for the whole app: the embedded CRM (same origin) reads
// `crm_locale` and re-applies it on its `storage` event, so it follows the
// console's language instead of keeping one of its own.
writeCrmLocale(i18n.language);
document.documentElement.lang = i18n.language;
i18n.on('languageChanged', (lng) => {
  writeCrmLocale(lng);
  document.documentElement.lang = lng;
});

// A language picked in another tab applies here too.
window.addEventListener('storage', (event) => {
  if (event.key !== LANGUAGE_STORAGE_KEY || !isSupportedLanguage(event.newValue)) return;
  if (i18n.language !== event.newValue) void i18n.changeLanguage(event.newValue);
});

export default i18n;
